import Enumerable from "linq";
import { Point, Size } from "../../../../../domain/drawing";
import { GraphSubItem, GraphValueItem } from "../../../../../domain/document/items";
import { LimitPair, HeightRangePair } from "../../../../../domain/system";
import { GraphScaleKinds } from "../../../../../domain/document";
import { LineInformation } from ".";

export class CoordinateCalculator {
  constructor(private _size: Size, private _scaleKind: GraphScaleKinds) {
  }

  calculate(subItems: GraphSubItem[]): LineInformation[] {
    const lines: LineInformation[] = [];
    for (const subItem of subItems) {
      const xRange = this.getXRange(subItem, subItems);
      const points = this.toPoints(subItem.graphValues, xRange, subItem.rangeOfValue);
      const line = new LineInformation();
      line.color = subItem.foreColor;
      line.points = points;
      lines.push(line);
    }
    return lines;
  }

  private getXRange(subItem: GraphSubItem, subItems: GraphSubItem[]): LimitPair<Date> {
    if (this._scaleKind === GraphScaleKinds.Common) {
      // 時間軸が共通
      const max = Enumerable.from(subItems)
        .selectMany(s => s.graphValues)
        .where(v => v.date != null && v.value != null)
        .select(v => v.date)
        .orderByDescending(d => d)
        .firstOrDefault();
      const min = Enumerable.from(subItems)
        .selectMany(s => s.graphValues)
        .where(v => v.date != null && v.value != null)
        .select(v => v.date)
        .orderBy(d => d)
        .firstOrDefault();

      if (!max || !min) { return null; }
      return new LimitPair(min, max);
    } else if (this._scaleKind === GraphScaleKinds.Individual) {
      // 時間軸が個別
      const max = Enumerable.from(subItem.graphValues)
        .where(v => v.date != null && v.value != null)
        .select(v => v.date)
        .orderByDescending(d => d)
        .firstOrDefault();
      const min = Enumerable.from(subItem.graphValues)
        .where(v => v.date != null && v.value != null)
        .select(v => v.date)
        .orderBy(d => d)
        .firstOrDefault();

      if (!max || !min) { return null; }
      return new LimitPair(min, max);
    }
    // 未知のスケール
    return null;
  }

  private toPoints(values: GraphValueItem[], xRange: LimitPair<Date>, yRange: HeightRangePair<number>): Point[] {
    if (!xRange) { return []; }
    const points: Point[] = [];
    for (const value of values) {
      const x = this.toX(value.date, xRange);
      const y = this.toY(value.value, yRange);
      if (x == null || y == null) { continue; }
      const point = new Point(x, y);
      Object.freeze(point);
      points.push(point);
    }
    return Enumerable.from(points)
      .orderBy(p => p.x)
      .toArray();
  }

  private toX(date: Date, range: LimitPair<Date>): number {
    if (!range || !date) { return null; }
    if (range.min === range.max) { return 0; }
    const max = range.max.getTime();
    const min = range.min.getTime();
    const width = max - min;
    if (width === 0) { return 0; }
    const value = date.getTime();
    return ((value - min) * this._size.width / width);
  }

  private toY(value: number, range: HeightRangePair<number>): number {
    if (value == null) { return null; }
    const height = range.high - range.low;
    if (height <= 0) { return null; }
    return this._size.height - ((value - range.low) * this._size.height / height);
  }

  getHorizontalDivideLines(horizontalDivideCount: number): LineInformation[] {
    if (horizontalDivideCount == null || horizontalDivideCount === 0) { return []; }
    const lines: LineInformation[] = [];
    const distance = this._size.height / horizontalDivideCount;
    for (let index = 1; index < horizontalDivideCount; ++index) {
      const points: Point[] = [];
      points.push(new Point(0, distance * index));
      points.push(new Point(this._size.width, distance * index));
      
      // 色はリアクティブにする必要が無いのでcssで指定している
      const line = new LineInformation();
      line.points = points;
      lines.push(line);
    }
    return lines;
  }
}
